import { useCallback, useState } from 'react';

export type ContactFormStatus = 'idle' | 'submitting' | 'success' | 'error';

export interface ContactFormValues {
  name: string;
  email: string;
  message: string;
  /** Honeypot — must stay empty. Hidden from real visitors via CSS, bots fill every field they see. */
  company: string;
}

const initialValues: ContactFormValues = { name: '', email: '', message: '', company: '' };

const API_URL = import.meta.env.VITE_CONTACT_API_URL;

/** Whether the contact-form API is configured (set post-deploy, see .env.example). */
export const isContactFormConfigured = Boolean(API_URL);

export const useContactForm = () => {
  const [values, setValues] = useState<ContactFormValues>(initialValues);
  const [status, setStatus] = useState<ContactFormStatus>('idle');

  const setField = useCallback((field: keyof ContactFormValues, value: string) => {
    setValues((v) => ({ ...v, [field]: value }));
  }, []);

  const submit = useCallback(async () => {
    if (!API_URL) return;
    setStatus('submitting');
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error('request_failed');
      setStatus('success');
      setValues(initialValues);
    } catch {
      setStatus('error');
    }
  }, [values]);

  return { values, setField, status, submit };
};
